import { FormattedMessage, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'

import { CopyToClipboard, CopyToClipboardWrapper } from '@10xLabs/react-web/components/CopyToClipboard'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { StyledLink } from 'components/v2/Link'
import { TableContainer, Table, Thead, Th, Tbody, Tr, Td } from 'components/v2/Table'
import { SecondaryInfo } from 'features/App/components/SecondaryInfo'
import { ConciliationStatus } from 'features/Inventories/components/ConciliationStatus'
import { OrderItems } from 'features/Inventories/components/OrderItems'
import { OrderStatus } from 'features/Inventories/components/OrderStatus'
import { orderTypeMessages } from 'features/Inventories/components/OrderType/messages'
import { TYPE } from 'features/Inventories/components/Type/constants'
import { isPending } from 'resolvers/orders'
import { safeFormatMessage } from 'utils/intl'
import { isSuperAdmin } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'

const getOriginLink = (order: TSFixMe): TSFixMe => {
  if (order.origin.type === TYPE.CART) {
    if (isPending(order)) {
      return `/carts/${order.origin.id}/pending-orders`
    } else {
      return `/carts/${order.origin.id}/outbound-orders`
    }
  } else {
    if (isPending(order)) {
      return `/inventories/${order.origin.id}/pending-orders`
    } else {
      return `/inventories/${order.origin.id}/outbounds`
    }
  }
}

const getDestinationLink = (order: TSFixMe): TSFixMe => {
  if (order.destination.type === TYPE.CART) {
    if (isPending(order)) {
      return `/carts/${order.destination.id}/pending-orders`
    } else {
      return `/carts/${order.destination.id}/inbound-orders`
    }
  } else {
    if (isPending(order)) {
      return `/inventories/${order.destination.id}/pending-orders`
    } else {
      return `/inventories/${order.destination.id}/inbounds`
    }
  }
}

const UserCell = ({ user }: TSFixMe): TSFixMe => {
  if (user && user.id) {
    return (
      <span>
        {user.firstName} {user.lastName} ({user.username})
      </span>
    )
  }

  return <span>-</span>
}

export const OrdersList = ({ orders = [] }): TSFixMe => {
  const intl = useIntl()
  const login = useLogin()

  const showCompany = isSuperAdmin(login.accessToken.conditions)

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            <Th>
              <FormattedMessage id="dictionary.id" defaultMessage="Id" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.dictionary.type" defaultMessage="Tipo" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.dictionary.status" defaultMessage="Estado" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.dictionary.createdAt" defaultMessage="Fecha de creación" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.orders.list.updatedAt.header" defaultMessage="Fecha de modificación" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.orders.list.origin.header" defaultMessage="Origen" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.orders.list.destination.header" defaultMessage="Destino" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.orders.list.items.header" defaultMessage="Productos" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.orders.list.createdBy.header" defaultMessage="Agregado por" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.orders.list.receivedBy.header" defaultMessage="Recibido por" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.orders.list.changedBy.header" defaultMessage="Modificado por" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.orders.list.cancelledBy.header" defaultMessage="Cancelado por" />
            </Th>

            <Th>
              <FormattedMessage id="spanish.orders.list.receiverApp.header" defaultMessage="App usada" />
            </Th>

            <Th>
              <FormattedMessage
                id="spanish.orders.list.conciliationStatus.header"
                defaultMessage="Estado de conciliación"
              />
            </Th>

            {showCompany && (
              <Th>
                <FormattedMessage id="spanish.dictionary.companies" defaultMessage="Compañías" />
              </Th>
            )}
          </Tr>
        </Thead>

        <Tbody>
          {orders.map((order: TSFixMe): TSFixMe => {
            const updatedAt = order.receivedAt || order.rejectedAt

            return (
              <Tr key={order.id}>
                <Td>
                  <CopyToClipboardWrapper>
                    <div>{order.id.substring(0, 8)}</div>

                    <CopyToClipboard str={order.id} />
                  </CopyToClipboardWrapper>
                </Td>

                <Td>{safeFormatMessage({ intl, messages: orderTypeMessages, key: order.type })}</Td>

                <Td>
                  <OrderStatus status={order.status} />
                </Td>

                <Td>
                  <FormattedDate intl={intl} value={order.createdAt} month="2-digit" day="2-digit" year="numeric" />

                  <SecondaryInfo>
                    <FormattedTimeByDate intl={intl} date={order.createdAt} />
                  </SecondaryInfo>
                </Td>

                <Td>
                  {updatedAt ? (
                    <>
                      <FormattedDate intl={intl} value={updatedAt} month="2-digit" day="2-digit" year="numeric" />

                      <SecondaryInfo>
                        <FormattedTimeByDate intl={intl} date={updatedAt} />
                      </SecondaryInfo>
                    </>
                  ) : (
                    '-'
                  )}
                </Td>

                <Td>
                  {order.origin && order.origin.id ? (
                    <StyledLink as={NavLink} to={getOriginLink(order)}>
                      {order.origin.name}
                    </StyledLink>
                  ) : (
                    order.supplier
                  )}
                </Td>

                <Td>
                  <StyledLink as={NavLink} to={getDestinationLink(order)}>
                    {order.destination.name}
                  </StyledLink>
                </Td>

                <Td>
                  <OrderItems order={order} />
                </Td>

                <Td>
                  <UserCell user={order.createdBy} />
                </Td>

                <Td>
                  <UserCell user={order.receivedBy} />
                </Td>

                <Td>
                  <UserCell user={order.changedBy} />
                </Td>

                <Td>
                  <UserCell user={order.cancelledBy} />
                </Td>

                <Td>{order.receiverApp ? order.receiverApp : '-'}</Td>

                <Td>{order.conciliationStatus ? <ConciliationStatus status={order.conciliationStatus} /> : 'N/A'}</Td>

                {showCompany && (
                  <Td>
                    {order.companies.map((company: TSFixMe): TSFixMe => {
                      return <div key={company.id}>{company.name}</div>
                    })}
                  </Td>
                )}
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
